import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Activity, ActivityFilters } from '@/types/activity';
import { applyActivityFilters } from '@/utils/activityFilters';

type SelectionMode = 'all-filtered' | 'custom';
const defaultIsSelectable = () => true;

function areSetsEqual(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) {
    return false;
  }

  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }

  return true;
}

interface UseFilteredActivitySelectionOptions {
  activities: Activity[];
  filters: ActivityFilters;
  isSelectable?: (activity: Activity) => boolean;
}

export function useFilteredActivitySelection({
  activities,
  filters,
  isSelectable = defaultIsSelectable,
}: UseFilteredActivitySelectionOptions) {
  const filteredActivities = useMemo(
    () => applyActivityFilters(activities, filters),
    [activities, filters]
  );
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('all-filtered');
  const [selectedActivityIds, setSelectedActivityIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const filteredIds = new Set(
      filteredActivities
        .filter(isSelectable)
        .map((activity) => activity.id)
    );

    setSelectedActivityIds((prev) => {
      if (selectionMode === 'all-filtered') {
        return areSetsEqual(prev, filteredIds) ? prev : filteredIds;
      }

      const nextSelectedIds = new Set(
        Array.from(prev).filter((id) => filteredIds.has(id))
      );
      return areSetsEqual(prev, nextSelectedIds) ? prev : nextSelectedIds;
    });
  }, [filteredActivities, isSelectable, selectionMode]);

  const selectedActivities = useMemo(
    () => filteredActivities.filter((activity) => selectedActivityIds.has(activity.id)),
    [filteredActivities, selectedActivityIds]
  );

  const toggleActivitySelection = useCallback((id: string) => {
    const activity = filteredActivities.find((candidate) => candidate.id === id);
    if (!activity || !isSelectable(activity)) {
      return;
    }

    setSelectionMode('custom');
    setSelectedActivityIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, [filteredActivities, isSelectable]);

  const selectAllActivities = useCallback(() => {
    setSelectionMode('all-filtered');
    setSelectedActivityIds(
      new Set(
        filteredActivities
          .filter(isSelectable)
          .map((activity) => activity.id)
      )
    );
  }, [filteredActivities, isSelectable]);

  const deselectAllActivities = useCallback(() => {
    setSelectionMode('custom');
    setSelectedActivityIds(new Set());
  }, []);

  const resetSelection = useCallback(() => {
    setSelectionMode('all-filtered');
  }, []);

  return {
    filteredActivities,
    selectedActivities,
    selectedActivityIds,
    toggleActivitySelection,
    selectAllActivities,
    deselectAllActivities,
    resetSelection,
  };
}
